import React, { useState, useRef, useEffect, useCallback } from "react";
import ChatBotWindow from "./ChatBotWindow";
import ChatErrorBoundary from "./ChatErrorBoundary";
import useSoundEffects from "./useSoundEffects";

const DEFAULT_API_URL = process.env.NODE_ENV === "production" ? window.location.origin : "http://localhost:8000";

const ChatBot = ({ apiUrl = process.env.REACT_APP_API_URL || DEFAULT_API_URL, onOpenContactModal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [launcherVisible, setLauncherVisible] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    try {
      const stored = window.localStorage.getItem("myboq-chat-language");
      if (stored && (stored === "english" || stored === "marathi")) return stored;
    } catch (e) {}
    return "english";
  });
  const launcherRef = useRef(null);
  const windowRef = useRef(null);
  const welcomeTimeoutRef = useRef(null);

  const welcomeMessages = {
    english: {
      greeting: "Hi there! I'm ",
      botName: "BOQ AI",
      description: "Ask me anything about MyBOQ, estimation, SSR rates, or pricing."
    },
    marathi: {
      greeting: "नमस्कार! मी ",
      botName: "BOQ AI",
      description: "मला MyBOQ, अंदाज, SSR दर, किंवा किंमत बद्दल काहीही विचारा."
    }
  };

  // Sync language from localStorage on storage events (e.g., when changed in chat window)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "myboq-chat-language" && e.newValue) {
        setCurrentLanguage(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const currentWelcome = welcomeMessages[currentLanguage] || welcomeMessages.english;

  const { playOpen, playClose, playReceive, playNotification, setEnabled } = useSoundEffects();

  useEffect(() => {
    setEnabled(true);
    setLauncherVisible(true);
    
    const hasVisited = window.localStorage.getItem("myboq-chat-visited");
    if (!hasVisited) {
      window.localStorage.setItem("myboq-chat-visited", "true");
      
      welcomeTimeoutRef.current = setTimeout(() => {
        setShowWelcome(true);
        playNotification();
        
        setTimeout(() => {
          setIsOpen(true);
          setIsMinimized(false);
          playOpen();
        }, 1500);
      }, 2000);
    }
    
    return () => {
      if (welcomeTimeoutRef.current) {
        clearTimeout(welcomeTimeoutRef.current);
      }
    };
  }, [setEnabled, playNotification, playOpen]);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
  }, []);

  const toggleChat = useCallback(() => {
    const next = !isOpen;
    setIsOpen(next);
    setIsMinimized(false);
    setUnreadCount(0);
    setHasNewMessage(false);
    next ? playOpen() : playClose();
  }, [isOpen, playOpen, playClose]);

  const handleMinimize = useCallback(() => {
    setIsMinimized(true);
    setIsOpen(false);
  }, []);

  const handleNewMessage = useCallback((isUser) => {
    if (!isUser) {
      playReceive();
      if (!isOpen && !isMinimized) {
        setUnreadCount((c) => c + 1);
        setHasNewMessage(true);
      }
    }
  }, [isOpen, isMinimized, playReceive]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    playClose();
  }, [playClose]);

  const handleFullscreenChange = useCallback((val) => {
    setIsFullscreen(val);
  }, []);

  return (
    <>
      {/* Launcher container — hidden in fullscreen so it doesn't overlay the chat */}
      <div
        className={`chatbot-launcher-container fixed bottom-[90px] sm:bottom-[100px] right-4 sm:right-6 z-[999] flex flex-col items-end gap-2 group transition-opacity duration-300 ${isFullscreen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        ref={launcherRef}
      >
        {showWelcome && !isOpen && !isMinimized && (
          <div
            className="animate-bounce-in absolute bottom-16 right-0 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-2xl border border-slate-700/60 max-w-xs z-50"
            role="dialog"
            aria-label="Welcome to BOQ AI"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-white">{currentWelcome.greeting} <span className="text-orange-300">{currentWelcome.botName}</span></p>
                <p className="text-slate-300 text-sm mt-1">{currentWelcome.description}</p>
              </div>
              <button
                onClick={dismissWelcome}
                className="text-slate-400 hover:text-white transition-colors p-1 flex-shrink-0"
                aria-label="Dismiss welcome message"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="absolute bottom-[-6px] right-6 w-0 h-0 border-6 border-transparent border-t-slate-900" />
          </div>
        )}

        {isMinimized && (
          <div
            className="animate-slide-up bg-slate-900/95 backdrop-blur-md text-white rounded-2xl shadow-xl border border-slate-700/60 px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-slate-800 transition-all duration-200"
            onClick={() => {
              setIsMinimized(false);
              setIsOpen(true);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && (setIsMinimized(false), setIsOpen(true))}
            aria-label={currentLanguage === "marathi" ? "BOQ AI चॅटबॉट पुन्हा उघडा" : "Restore BOQ AI Chatbot"}
          >
            <div className="w-2.5 h-2.5 bg-orange-400 rounded-full animate-pulse shadow-sm shadow-orange-400/50" />
            <span className="text-xs font-semibold text-slate-100">BOQ AI</span>
            <span className="text-[11px] text-slate-400">{currentLanguage === "marathi" ? "विस्तारित करण्यासाठी क्लिक करा" : "Click to expand"}</span>
          </div>
        )}

        <div className="relative">
          {/* Tooltip on the left side of launcher button */}
          <div className="absolute right-16 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap border border-slate-700/50">
            {isOpen 
              ? (currentLanguage === "marathi" ? "BOQ AI बंद करा" : "Close BOQ AI") 
              : (currentLanguage === "marathi" ? "BOQ AI विचारा" : "Ask BOQ AI")}
            {/* Arrow pointing right */}
            <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45 border-t border-r border-slate-700/50" />
          </div>

          <button
            ref={windowRef}
            onClick={toggleChat}
            className={`relative bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 text-white p-3.5 md:p-4 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 active:scale-95 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-orange-300/50 ${
              isOpen ? "rotate-90 bg-slate-800" : ""
            } ${launcherVisible ? "animate-bounce-in" : "opacity-0 scale-90"}`}
            aria-label={isOpen 
              ? (currentLanguage === "marathi" ? "चॅट बंद करा" : "Close chat") 
              : (currentLanguage === "marathi" ? "चॅट उघडा" : "Open chat")}
            aria-expanded={isOpen}
          >
            {/* Ping Animation when closed */}
            {!isOpen && (
              <span className="absolute inset-0 rounded-full bg-orange-400 opacity-40 animate-ping pointer-events-none" />
            )}
            
            <span className="relative z-10 flex items-center justify-center">
              {isOpen ? (
                <svg
                  className="w-6 h-6 text-white transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              )}
            </span>

            {/* Unread badge */}
            {unreadCount > 0 && !isOpen && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center animate-bounce border-2 border-white shadow-md">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}

            {/* New message indicator */}
            {hasNewMessage && !isOpen && (
              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full animate-pulse border-2 border-white shadow-sm" />
            )}
          </button>
        </div>
      </div>

      {/* Chat window rendered as a sibling so fullscreen truly covers the viewport */}
      <ChatErrorBoundary>
        <ChatBotWindow
          isOpen={isOpen}
          onClose={handleClose}
          onMinimize={handleMinimize}
          onNewMessage={handleNewMessage}
          apiUrl={apiUrl}
          onOpenContactModal={onOpenContactModal}
          initialLanguage={currentLanguage}
          onFullscreenChange={handleFullscreenChange}
        />
      </ChatErrorBoundary>
    </>
  );
};

export default ChatBot;